import _debounce from 'lodash.debounce';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import { COUNTRIES } from '../consts/countries';
import {
    geocodeWithFrenchFallback,
    resolveFrenchGeocodeParams,
} from '../utils';

Vue.component('configurator-dealer-search', {
    props: {
        initialSiteHandle: {
            type: String,
        },
        dormCountryHandle: {
            type: String,
        },
        stores: {
            value: Array,
        },
        brandstoreResultAmount: {
            value: Object,
        },
        passToParent: {
            value: Boolean,
            default: false,
        },
    },

    data() {
        return {
            siteHandle: '',

            selectedBrandStore: '',
            store: '',
            zipCoords: null,
            searchQueryIsDirty: false,
            nearbyProximityStores: null,

            cityNotFound: false,
            showDropdown: false,

            // Error vars for zip api request
            needMoreInput: false,
            apiRequestLoading: false,
            countryValue: COUNTRIES,

            geocoder: null,
        };
    },

    created() {
        EventBus.$on('configurator-stock-with-dealer', (data) => {
            this.selectedBrandStore = data;
            this.store = data.title;
        });
    },

    computed: {
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },

        toggleDropdownClass() {
            return this.zipCoords ? 'dropdown-active' : '';
        },
    },

    watch: {
        store() {
            //reset everything if there is no input
            if (this.store.length === 0) {
                this.resetErrorState();
                return;
            }

            //show error message that input has to be at least 3 carachters long
            if (this.store.length < 3) {
                this.showDropdown = false;
                this.cityNotFound = false;
                this.needMoreInput = true;
                return;
            }

            if (this.selectedBrandStore) {
                if (this.selectedBrandStore.title === this.store) {
                    return;
                }
            }

            this.needMoreInput = false;

            this.searchByZipGoogleApi();
        },

        selectedBrandStore(value) {
            if (value) this.showDropdown = false;
        },
    },

    mounted() {
        // Set sitehandle and update it if it changes.
        this.siteHandle = this.initialSiteHandle;

        EventBus.$on('country-changed', (siteHandle) => {
            this.siteHandle = siteHandle;
        });

        this.injectGoogleMaps();

        EventBus.$once('google-maps-ready', this.initMap);
    },

    methods: {
        initMap() {
            // Adding Google maps Geocoder
            this.geocoder = new google.maps.Geocoder();
        },

        injectGoogleMaps() {
            var tag = document.createElement('script');
            tag.setAttribute('type', 'text/javascript');
            tag.defer = true;
            tag.async = true;
            tag.setAttribute(
                'src',
                '//maps.google.com/maps/api/js?key=AIzaSyBXLteX7ErcVvjvFLHB1_TMCzq55wVb5Bg&libraries=places&callback=mapsCallback',
            );
            (
                document.getElementsByTagName('head')[0] ||
                document.documentElement
            ).appendChild(tag);
        },

        resetErrorState() {
            this.selectedBrandStore = null;
            this.showDropdown = false;
            this.needMoreInput = false;
            this.cityNotFound = false;
        },

        showBrandstoreSearch() {
            return (
                this.siteHandle != 'mgMotorsBelgiumNl' &&
                this.siteHandle != 'mgMotorsBelgiumFr' &&
                this.siteHandle != 'mgMotorsLuxembourgFr'
            );
        },

        selectedBrandStoreValue() {
            return this.selectedBrandStore
                ? JSON.stringify(this.selectedBrandStore)
                : null;
        },

        showZipError() {
            return (
                this.cityNotFound &&
                !this.apiRequestLoading &&
                !this.selectedBrandStore
            );
        },

        setCityNotFound() {
            this.showDropdown = false;
            this.needMoreInput = false;
            this.cityNotFound = true;
        },

        setInputLengthError() {
            this.needMoreInput = true;
            this.nearbyProximityStores = null;
        },

        searchByZipGoogleApi: _debounce(function () {
            this.makeApiRequest();
        }, 300),

        makeApiRequest() {
            this.apiRequestLoading = true;

            let tempStore = this.store;

            let code = this.countryValue[this.dormCountryHandle];
            if (!code) {
                code = this.countryValue[this.siteHandle];
            }

            if (this.siteHandle === 'mgMotorsFrance') {
                ({ address: tempStore, country: code } =
                    resolveFrenchGeocodeParams(this.store));
            }

            const handleGeocodeResult = (results, status) => {
                if (status === 'OK' && results.length) {
                    const [res] = results;
                    this.apiRequestLoading = false;
                    const isDefaultLocation =
                        res.address_components.length === 1 &&
                        res.address_components[0].short_name === code;

                    //check if geocoder returns default coordination - no results for search
                    if (isDefaultLocation) {
                        this.setCityNotFound();
                        return;
                    }

                    this.showDropdown = true;

                    this.zipCoords = {
                        lat: res.geometry.location.lat(),
                        lng: res.geometry.location.lng(),
                    };

                    if (!this.zipCoords || this.stores.length < 1) return;

                    this.getNearbyBrandstores(this.zipCoords, this.stores);
                    return;
                }
                this.apiRequestLoading = false;
                this.setCityNotFound();
                console.error(
                    'Geocode was not successful for the following reason: ' +
                        status,
                );
            };

            geocodeWithFrenchFallback(
                this.geocoder,
                { address: tempStore, country: code },
                this.siteHandle,
                handleGeocodeResult,
            );
        },

        getNearbyBrandstores(userLocation, brandstores) {
            const storesWithDistance = Object.values(brandstores).map(
                (store) => {
                    const distance = this.getDistance(userLocation, store);
                    return {
                        ...store,
                        distance,
                    };
                },
            );

            this.nearbyProximityStores = storesWithDistance
                .sort((a, b) => {
                    if (a.distance < b.distance) {
                        return -1;
                    }

                    if (a.distance > b.distance) {
                        return 1;
                    }

                    return 0;
                })
                .slice(0, this.brandstoreResultAmount);
        },

        getDistance(userLocation, brandstore) {
            // Bron: https://cloud.google.com/blog/products/maps-platform/how-calculate-distances-map-maps-javascript-api
            var radius = 6371.071; // Radius of the Earth in KM

            const radiusLat1 = userLocation.lat * (Math.PI / 180); // Convert degrees to radians
            const radiusLat2 = brandstore.lat * (Math.PI / 180); // Convert degrees to radians
            const differenceLat = radiusLat2 - radiusLat1; // Radian difference (latitudes)
            const differenceLng =
                (brandstore.lng - userLocation.lng) * (Math.PI / 180); // Radian difference (longitudes)

            const distance =
                2 *
                radius *
                Math.asin(
                    Math.sqrt(
                        Math.sin(differenceLat / 2) *
                            Math.sin(differenceLat / 2) +
                            Math.cos(radiusLat1) *
                                Math.cos(radiusLat2) *
                                Math.sin(differenceLng / 2) *
                                Math.sin(differenceLng / 2),
                    ),
                );

            return distance;
        },

        replaceInputValue(e) {
            e.preventDefault();
            this.selectedBrandStore = this.stores[e.target.dataset.value];
            this.store = this.selectedBrandStore.title;

            // Reset zipCoords to null
            this.zipCoords = null;

            if (!this.passToParent) {
                return;
            }

            this.$emit('selected-brandstore', this.selectedBrandStore);
        },
    },
});
